import { useState } from 'react';
import type { NextPage } from 'next';
import Head from '../head';
import AllLayout from '../all-layout';
import SearchListing from '../search-listing';
import NavCategoryDrawer from '../nav-category-drawer';
import CategorySectionHeader from '../category-section-header';
import LocalSearchSwitch from '../local-search-switch';
import { useProduct, useCategory, useRouterContext } from '../../contexts';
import { useSearch } from '../../contexts/SearchProvider';
import { useScrollRestoration } from '../../utils/useScrollRestoration';
import shared from '../../data/js/shared';

import { BrowseCategoryProps } from './types';
import { useScrollClassToggle } from './useScrollClassToggle';
import { useBrowseListings } from './useBrowseListings';
import { useCategoryTagPills } from './useCategoryTagPills';
import CategoryTagPills from './CategoryTagPills';

const { categoryHeading } = shared.browse;

const BrowseCategoryPage: NextPage<BrowseCategoryProps> = (props) => {
  const { searchParams, searchEmpty } = useSearch();
  const { currentList, replaceCurrentList } = useProduct();
  const { getCategoryBySlug } = useCategory();
  const { query, asPath, isReady } = useRouterContext();

  // Strip the dynamic route segments from `query`; everything else (q, filters)
  // is a real search param. Query-string params populate immediately, so they
  // stay sourced from `query`.
  const { category: categoryParam, subcategory: _subcategory, ...urlParams } = query;

  // The router's query is the whole app's, and while a navigation is under way
  // it already holds the next page's: on the way to a listing, and again when
  // Back mounts this page, it read { id, slug } as filters. The page fetched
  // with them, and on Back that stray fetch landed over the list Back had put
  // back, cutting it to page 1 and leaving the reader at the footer
  // (2026-10-20). The URL is this page's only while it is a browse URL.
  const onThisPage = asPath?.startsWith('/browse/') ?? false;

  // Category slug is derived from `asPath`, NOT `query.category`. In the
  // Capacitor static export, dynamic route params in `query` lag (or stay
  // empty) after a client-side navigation — only `asPath` (the raw URL)
  // updates reliably. That's the exact asymmetry behind the bug: page-view
  // tracking keys off `asPath` and updates per tap, while anything reading
  // `query.category` stayed pinned to the first category and never refetched.
  // Path shape: `/browse/<category>` (mobile resolves subcategory slugs here
  // too) or `/browse/<category>/<subcategory>` on web. Take the LAST segment
  // after `browse` -- the subcategory when there is one. Taking the first put
  // the parent group on every subcategory page whose group slug is known:
  // /browse/electronics/cameras read "Browse Electronics" and searched all of
  // Electronics (2026-06-11 to 2026-10-02). Slugs are unique across groups and
  // subcategories, so the last segment alone identifies the category.
  // query/props remain fallbacks for the first paint.
  const pathSegments = (asPath || '').split('?')[0].split('/').filter(Boolean);
  const slugFromPath = pathSegments[0] === 'browse' ? (pathSegments[2] ?? pathSegments[1]) : undefined;
  const category =
    (slugFromPath && decodeURIComponent(slugFromPath)) ||
    (_subcategory as string) ||
    (categoryParam as string) ||
    props.categorySlug ||
    props.catName;

  // Pagination state (shared with SearchListing + restoration hooks)
  const [page, setPage] = useState(1);
  const [done, setDone] = useState(false);

  // Scroll position + pagination restoration on back/forward navigation
  useScrollRestoration({ page, setPage, done, setDone });
  useScrollClassToggle('scrolling');

  // Category identity is derived from the URL slug, NOT server props. On mobile
  // (Capacitor static export) there is no per-navigation SSR, so `props` are
  // baked at build time and never refresh on client navigation — keying off them
  // would pin the page to the first-loaded category and the list would never
  // update. The slug lookup is pure static data (works identically on mobile and
  // web), so it's the reliable source of truth; props are only a fallback for the
  // rare slug-lookup miss.
  const categoryData = getCategoryBySlug(category as string);
  const isSubcategory = categoryData
    ? Boolean(categoryData.parentId)
    : (props.isSubcategory ?? false);
  // The id we search by: subcategory id for subcategories, group id otherwise.
  const effectiveId =
    categoryData?.id ??
    (isSubcategory ? props.currentCategoryId : props.currentCategoryGroupId) ??
    0;
  // Parent group id (nav highlight + group-level searches).
  const categoryGroupId = isSubcategory
    ? (categoryData?.parentId ?? props.currentCategoryGroupId ?? 0)
    : effectiveId;
  const name = categoryData?.label || props.catName || '';

  // Single owner of listing population (category init + search sync + fetch).
  useBrowseListings({
    initialList: props.currentList || [],
    effectiveId,
    isSubcategory,
    currentCategoryId: isSubcategory ? effectiveId : props.currentCategoryId,
    currentCategoryGroupId: categoryGroupId,
    urlParams,
    ready: isReady && onThisPage,
    setPage,
    setDone,
  });

  // Related tags, counted across the whole group so a subcategory shows its group's pills.
  // Live everywhere since 2026-10-10 (the owner: "show the tag pills on prod now").
  const tagPills = useCategoryTagPills(categoryGroupId);

  return (
    <AllLayout header={true} search={true} nav={true} showAd={true}>
      <Head title={`Browse ${name}`} />
      <article className="page same-color">
        <NavCategoryDrawer />
        <LocalSearchSwitch />
        <SearchListing
          searchEmpty={searchEmpty}
          belowHeading={<CategoryTagPills pills={tagPills} />}
          heading={
            <CategorySectionHeader
              categorySlug={category as string}
              categoryName={name}
              categoryHeading={categoryHeading}
              sortBy={query.sortBy}
            />
          }
          page={page}
          setPage={setPage}
          done={done}
          setDone={setDone}
          focusedSubcategory={props.focusedSubcategory}
          showcaseData={props.showcaseData}
          categoryGroupLabel={props.categoryGroupLabel}
          parentCategorySlug={!isSubcategory ? props.categorySlug : undefined}
          sortable
        />
      </article>
    </AllLayout>
  );
};

export default BrowseCategoryPage;
